import React, { useCallback, memo, useMemo } from "react";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import clsx from "clsx";

import { IInputTextProps } from "./interfaces";
import { IFieldStatus } from "common-interfaces";
import { useStyles } from "./input-text.styles";

const getInputAdornment = (status: IFieldStatus) => {
  if (status === "ERROR") {
    return (
      <InputAdornment position="end">
        <ErrorOutlineIcon />
      </InputAdornment>
    );
  }

  if (status === "SUCCESS") {
    return (
      <InputAdornment position="end">
        <CheckCircleOutlineIcon />
      </InputAdornment>
    );
  }
};

export const InputText = memo(
  ({
    label,
    name,
    id,
    value,
    status,
    type = "text",
    isFullWidth = false,
    isMultiline = false,
    isDisabled = false,
    helperText = " ",
    rows = 1,
    startAdornment = null,
    className = undefined,
    onChange,
    onBlur,
    onFocus,
  }: IInputTextProps) => {
    const classes = useStyles();

    const handleBlur = useCallback(
      (evt) => {
        onBlur && onBlur(evt);
      },
      [onBlur]
    );

    const handleFocus = useCallback(
      (evt) => {
        onFocus && onFocus(evt);
      },
      [onFocus]
    );

    // Permet de ne pas avoir l'autocomplétion de chrome
    const randomisedName = `${name}-${Math.random()}`;

    const computedClassName = useMemo(() => {
      return clsx(classes.root, status, className);
    }, [className, status, classes.root]);

    return (
      <TextField
        className={computedClassName}
        type={type}
        label={label}
        name={randomisedName}
        id={id}
        disabled={isDisabled}
        value={value}
        fullWidth={isFullWidth}
        multiline={isMultiline}
        helperText={helperText}
        rows={rows}
        onChange={onChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        variant="outlined"
        InputProps={{
          endAdornment: getInputAdornment(status),
          startAdornment,
        }}
      />
    );
  }
);
